/**
 * Move keyboard focus into a freshly-mounted sheet / card.
 *
 * Both the standalone rail (useSheetStack) and the transcript modal's
 * CapturedSections call this on every "+ Add" push and every
 * status/name-click update push, so the reviewer's next keystroke lands
 * inside the new card without a mouse trip.
 *
 * Rules:
 *   • Skip disabled controls — a disabled first-child (grey Save
 *     button rendered by an empty add sheet) would eat the focus and
 *     leave nothing typeable.
 *   • Skip `tabindex="-1"` — used for scroll targets, not inputs.
 *   • Wait a frame — many card bodies run their own mount effects
 *     (async catalog fetch, autofocus flags, dropdown open state).
 *     Focusing before those settle can be immediately overridden.
 *   • Best-effort — swallow errors so a caller's ref-callback never
 *     bubbles a focus problem up into React's render.
 */

const FOCUSABLE = [
  "input:not([disabled]):not([tabindex=\"-1\"])",
  "textarea:not([disabled]):not([tabindex=\"-1\"])",
  "select:not([disabled]):not([tabindex=\"-1\"])",
  "button:not([disabled]):not([tabindex=\"-1\"])",
  "[contenteditable]:not([tabindex=\"-1\"])",
  "[tabindex]:not([tabindex=\"-1\"]):not([disabled])",
].join(",");

/**
 * Focus the first focusable descendant of `node`.
 *
 * `node` — the sheet / card wrapper DOM node registered by the caller's
 *   ref callback. Silently no-ops when `node` is null or has no
 *   focusable descendant (e.g. a read-only View modal).
 */
const focusFirstFocusable = (node) => {
  if (!node || typeof node.querySelector !== "function") return;
  // rAF instead of setTimeout(_, 0) — rAF fires AFTER React has
  // committed but BEFORE the browser paints, so the focus ring lands
  // on the intended input before the user sees the new card.
  const raf = window.requestAnimationFrame || ((cb) => setTimeout(cb, 0));
  raf(() => {
    if (!node.isConnected) return; // sheet was closed between push and focus
    const el = node.querySelector(FOCUSABLE);
    if (!el || typeof el.focus !== "function") return;
    try { el.focus({ preventScroll: false }); } catch (_) { /* best-effort */ }
  });
};

export default focusFirstFocusable;
